import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Superadmin override (owner 2026-09-23). Change any lock — client broker / Direct,
// booking approval, EOI partner decision, EOI client confirmation, prior intimation —
// with a written reason. Every change is logged (below) and cannot be hidden.
const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 12 };
const pill = (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg });
const btn = { padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, marginRight: 6, marginTop: 6 };
const when = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const LABEL = {
  client_attribution: 'Client broker / Direct', sale_approval: 'Booking approval', eoi_partner_decision: 'EOI partner decision',
  eoi_confirmation: 'EOI client confirmation', intimation: 'Prior intimation',
};

const Overrides = ({ apiBase, user }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(null);     // { client } while choosing a broker
  const [pq, setPq] = useState('');
  const [partners, setPartners] = useState([]);
  const isSuper = ['superadmin', 'super_admin'].includes(String(user?.role || '').toLowerCase());

  const loadLog = useCallback(() => axios.get(`${apiBase}/overrides/log`).then((r) => setLog(r.data)).catch(() => {}), [apiBase]);
  const search = useCallback(() => {
    if (q.trim().length < 2) { setRows([]); return; }
    setBusy(true);
    axios.get(`${apiBase}/overrides/search`, { params: { q } }).then((r) => setRows(r.data)).catch((e) => alert(e.response?.data?.error || 'Search failed')).finally(() => setBusy(false));
  }, [apiBase, q]);
  useEffect(() => { if (isSuper) loadLog(); }, [isSuper, loadLog]);
  useEffect(() => {
    if (!pick) return;
    const t = setTimeout(() => axios.get(`${apiBase}/overrides/partners`, { params: { q: pq } }).then((r) => setPartners(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [apiBase, pick, pq]);

  if (!isSuper) return <div style={{ padding: 24 }}>Superadmin only.</div>;

  const apply = async (kind, id, value, what) => {
    const reason = window.prompt(`${LABEL[kind]}: ${what}\n\nWrite the reason for this override (it is logged with your name and the time):`);
    if (reason === null) return;
    if (reason.trim().length < 5) { alert('Please write a proper reason.'); return; }
    try {
      await axios.post(`${apiBase}/overrides/apply`, { kind, id, value, reason });
      setPick(null); search(); loadLog();
    } catch (e) { alert(e.response?.data?.error || 'Could not apply the override.'); }
  };

  const attrib = (c) => (c.attribution === 'direct' ? <span style={pill('#e0f2fe', '#075985')}>Direct</span>
    : c.channel_partner_id ? <span style={pill('#fef3c7', '#92400e')}>Broker: {c.cp_name}{c.cp_firm ? `, ${c.cp_firm}` : ''}</span>
    : <span style={pill('#f3f4f6', '#374151')}>Not locked yet</span>);
  const stat = (s) => {
    const c = { approved: ['#dcfce7', '#166534'], legacy: ['#dcfce7', '#166534'], confirmed: ['#dcfce7', '#166534'], rejected: ['#fee2e2', '#991b1b'],
      pending: ['#fef3c7', '#92400e'], awaiting: ['#fef3c7', '#92400e'], changes_requested: ['#fee2e2', '#991b1b'], open: ['#fef3c7', '#92400e'] }[s] || ['#f3f4f6', '#374151'];
    return <span style={pill(c[0], c[1])}>{s || 'none'}</span>;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h2 style={{ margin: '0 0 4px' }}>Superadmin override</h2>
      <p style={{ color: '#6b7280', marginTop: 0 }}>Change a locked record. Every change needs a reason and is logged with your name and the time.</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Client name, mobile, EOI no. or flat"
          style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15 }} />
        <button style={{ ...btn, marginTop: 0, background: '#111827', color: '#fff' }} onClick={search} disabled={busy}>{busy ? 'Searching…' : 'Search'}</button>
      </div>

      {rows.map((c) => (
        <div key={c.id} style={box}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div><b style={{ fontSize: 16 }}>{c.name}</b> <span style={{ color: '#6b7280' }}>{c.phone}</span></div>
            <div>{attrib(c)} {c.attribution_proof && <span style={{ color: '#6b7280', fontSize: 12 }}>proof: {c.attribution_proof} {when(c.attribution_locked_at)}</span>}</div>
          </div>
          <div>
            <button style={btn} onClick={() => apply('client_attribution', c.id, 'direct', `make ${c.name} DIRECT (removes the broker from the client, EOIs, bookings and walk-ins)`)}>Make Direct</button>
            <button style={btn} onClick={() => { setPick(c); setPq(''); }}>Change broker…</button>
          </div>
          {pick?.id === c.id && (
            <div style={{ marginTop: 8, padding: 10, background: '#f9fafb', borderRadius: 8 }}>
              <input autoFocus value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search channel partner" style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db' }} />
              {partners.map((p) => (
                <div key={p.id} style={{ padding: '6px 4px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onClick={() => apply('client_attribution', c.id, p.id, `tag ${c.name} to broker ${p.name}${p.firm ? `, ${p.firm}` : ''}`)}>
                  {p.name}{p.firm ? `, ${p.firm}` : ''} <span style={{ color: '#6b7280' }}>{p.phone}</span>
                </div>
              ))}
              <button style={btn} onClick={() => setPick(null)}>Cancel</button>
            </div>
          )}

          {c.sales.map((s) => (
            <div key={`s${s.id}`} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
              <b>Booking {s.unit_number}</b> {stat(s.approval_status)} <span style={{ color: '#6b7280', fontSize: 13 }}>{s.approved_by_name} {when(s.approved_at)}</span>
              <div>
                <button style={btn} onClick={() => apply('sale_approval', s.id, 'pending', `reopen approval of booking ${s.unit_number} (partners can decide again)`)}>Reopen approval</button>
                <button style={btn} onClick={() => apply('sale_approval', s.id, 'approved', `approve booking ${s.unit_number} as superadmin`)}>Approve as superadmin</button>
              </div>
            </div>
          ))}

          {c.eois.map((e) => (
            <div key={`e${e.id}`} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
              <b>{e.eoi_number}</b> partners: {stat(e.partner_review_status)} <span style={{ color: '#6b7280', fontSize: 13 }}>{e.partner_decision_by} {when(e.partner_decision_at)}</span>
              {' '}· client: {stat(e.confirmation_status)} <span style={{ color: '#6b7280', fontSize: 13 }}>{when(e.confirmed_at)}</span>
              <div>
                <button style={btn} onClick={() => apply('eoi_partner_decision', e.id, 'reset', `reset the partner decision on ${e.eoi_number} (send to partners again)`)}>Reset partner decision</button>
                <button style={btn} onClick={() => apply('eoi_partner_decision', e.id, 'approved', `approve ${e.eoi_number} as superadmin`)}>Approve EOI</button>
                <button style={btn} onClick={() => apply('eoi_partner_decision', e.id, 'rejected', `reject ${e.eoi_number} as superadmin`)}>Reject EOI</button>
                <button style={btn} onClick={() => apply('eoi_confirmation', e.id, 'reset', `reset the client's confirmation on ${e.eoi_number} (client must confirm again)`)}>Reset client confirmation</button>
              </div>
            </div>
          ))}

          {c.intimations.map((i) => (
            <div key={`i${i.id}`} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
              <b>Prior intimation</b> by {i.cp_name} {stat(i.status)} <span style={{ color: '#6b7280', fontSize: 13 }}>{when(i.created_at)}</span>
              <div>
                <button style={btn} onClick={() => apply('intimation', i.id, 'cancelled', `cancel the intimation by ${i.cp_name}`)}>Cancel</button>
                <button style={btn} onClick={() => apply('intimation', i.id, 'open', `re-open the intimation by ${i.cp_name}`)}>Re-open</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {!busy && q.trim().length >= 2 && !rows.length && <p style={{ color: '#6b7280' }}>No client found — press Search.</p>}

      <h3 style={{ marginTop: 28 }}>Override log</h3>
      <div style={box}>
        {!log.length && <p style={{ color: '#6b7280', margin: 0 }}>No overrides yet.</p>}
        {log.map((l) => (
          <div key={l.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <div><b>{when(l.at)}</b> · {LABEL[String(l.table_name).replace('override:', '')] || l.table_name} #{l.row_id}</div>
            <div style={{ color: '#374151' }}>{l.by_user}</div>
            <details><summary style={{ cursor: 'pointer', color: '#6b7280' }}>Before / after</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, background: '#f9fafb', padding: 8, borderRadius: 6 }}>{JSON.stringify({ before: l.old_row, after: l.new_row }, null, 1)}</pre>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Overrides;
